import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { IdCard, Save, Package, Lock, User, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { api, apiErrorMessage } from "@/lib/api";
import { FloatingSaveButton } from "@/components/FloatingSaveButton";

const PERSONAL_FIELDS = [
  ["dni", "DNI / NIE", "text"],
  ["fecha_nacimiento", "Fecha de nacimiento", "date"],
  ["telefono_personal", "Teléfono personal", "text"],
  ["email_personal", "Email personal", "email"],
];
const ADDRESS_FIELDS = [
  ["via", "Vía / Calle", "wide"],
  ["numero", "Número", ""],
  ["piso", "Piso / Puerta", ""],
  ["cp", "Código Postal", ""],
  ["localidad", "Localidad", ""],
  ["provincia", "Provincia", ""],
];
const ALL_KEYS = [...PERSONAL_FIELDS.map((f) => f[0]), "contacto_emergencia", ...ADDRESS_FIELDS.map((f) => f[0])];

function Section({ title, icon: Icon, children }) {
  return (
    <div className="space-y-3 border-t pt-5 first:border-t-0 first:pt-0">
      <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {Icon && <Icon className="h-3.5 w-3.5" />}{title}
      </h3>
      {children}
    </div>
  );
}

export default function MiFichaTab() {
  const qc = useQueryClient();
  const meQ = useQuery({ queryKey: ["rrhh-mi-ficha"], queryFn: async () => (await api.get("/rrhh/profiles/me")).data });
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (meQ.data && !form) {
      const f = {};
      ALL_KEYS.forEach((k) => { f[k] = meQ.data[k] || ""; });
      setForm(f);
    }
  }, [meQ.data, form]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const saveMut = useMutation({
    mutationFn: async () => (await api.put("/rrhh/profiles/me", form)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["rrhh-mi-ficha"] }); toast.success("Ficha actualizada. ¡Gracias por mantener tus datos al día!"); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  if (meQ.isLoading || !form) return <Skeleton className="h-96 w-full" />;
  const inventario = meQ.data?.inventario || [];

  return (
    <div className="mx-auto max-w-3xl space-y-5 pb-24" data-testid="rrhh-mi-ficha">
      <FloatingSaveButton
        onClick={() => saveMut.mutate()}
        saving={saveMut.isPending}
        label="Guardar mis datos"
        testid="mf-save"
      />
      <div>
        <h1 className="flex items-center gap-2 font-heading text-2xl font-bold tracking-tight"><IdCard className="h-6 w-6 text-primary" />Mi ficha</h1>
        <p className="text-sm text-muted-foreground">Completa y mantén al día tus datos personales. Solo tú y RRHH podéis verlos.</p>
      </div>

      <Card><CardContent className="space-y-5 p-5">
        <Section title="Datos personales" icon={User}>
          <div className="grid gap-3 sm:grid-cols-2">
            {PERSONAL_FIELDS.map(([k, label, type]) => (
              <div key={k} className="space-y-1.5">
                <Label>{label}</Label>
                <Input type={type} value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`mf-field-${k}`} />
              </div>
            ))}
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Contacto de emergencia</Label>
              <Input value={form.contacto_emergencia} onChange={(e) => set("contacto_emergencia", e.target.value)} placeholder="Nombre y teléfono" data-testid="mf-field-contacto_emergencia" />
            </div>
          </div>
        </Section>

        <Section title="Dirección" icon={MapPin}>
          <div className="grid gap-3 sm:grid-cols-2">
            {ADDRESS_FIELDS.map(([k, label, mod]) => (
              <div key={k} className={`space-y-1.5 ${mod === "wide" ? "sm:col-span-2" : ""}`}>
                <Label>{label}</Label>
                <Input value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`mf-field-${k}`} />
              </div>
            ))}
          </div>
        </Section>

        <Section title="Material de empresa asignado" icon={Package}>
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <Lock className="h-3 w-3" />Gestionado por Administración (solo lectura).
          </div>
          {inventario.length === 0 ? (
            <p className="rounded-lg border border-dashed p-3 text-xs text-muted-foreground" data-testid="mf-inv-empty">No tienes material de empresa asignado.</p>
          ) : (
            <div className="space-y-2" data-testid="mf-inv-list">
              {inventario.map((it, i) => (
                <div key={i} className="flex items-center gap-3 rounded-lg border bg-muted/30 p-2.5" data-testid={`mf-inv-${i}`}>
                  <Package className="h-4 w-4 shrink-0 text-primary" />
                  <span className="font-medium">{it.material}</span>
                  {it.notas && <span className="text-xs text-muted-foreground">· {it.notas}</span>}
                </div>
              ))}
            </div>
          )}
        </Section>

        <div className="flex justify-end border-t pt-4">
          <p className="text-xs text-muted-foreground">Usa el botón <strong>Guardar</strong> flotante abajo a la derecha o pulsa <kbd>Ctrl+S</kbd>.</p>
        </div>
      </CardContent></Card>
    </div>
  );
}
